<script setup lang="ts">
  import { computed, ref } from 'vue';

  import type { Partner } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger,
  } from '@ui/components/dropdown-menu';
  import { toast } from '@ui/components/sonner';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import { stuut } from '@/api';
  import DisputeFormDialog from '@/components/disputes/DisputeFormDialog.vue';
  import DraftEmailButton from '@/components/emails/DraftEmailButton.vue';
  import DunningStateSelector from '@/components/partners/DunningStateSelector.vue';
  import PartnerAssigneeSelect from '@/components/partners/PartnerAssigneeSelect.vue';
  import PartnerTagSelect from '@/components/partners/PartnerTagSelect.vue';
  import { useProfile } from '@/store';
  import { getInitials } from '@/utils/exportHelpers';

  interface Props {
    hideEmailButton?: boolean;
    partner: Partner;
  }

  const props = defineProps<Props>();

  const profileStore = useProfile();

  const isSyncTriggered = ref(false);
  const showDisputeFormDialog = ref(false);

  const isSyncing = computed<boolean>(() => {
    if (!canSync.value) return false;

    return isSyncTriggered.value;
  });

  const canSync = computed<boolean>(() => {
    return props.partner.linked_integration?.integration_name !== 'file-sync';
  });

  const sync = async () => {
    if (isSyncing.value || !canSync.value) return;

    isSyncTriggered.value = true;
    try {
      await stuut.partners.sync(props.partner);
      toast.success('Customer successfully synced.');
    } catch (error: any) {
      toast.error(error.clientMessage || 'An error occurred');
    } finally {
      isSyncTriggered.value = false;
    }
  };
</script>

<template>
  <div class="w-full min-h-fit overflow-hidden">
    <div class="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between flex-wrap">
      <div class="flex flex-row gap-2.5 items-center truncate max-w-[60%]">
        <div class="flex items-center justify-center bg-agent-muted text-agent rounded-sm size-8">
          <strong>{{ getInitials(partner.computedName) }}</strong>
        </div>

        <div class="flex flex-col gap-y-0.5">
          <h3 class="text-lg font-bold truncate">
            {{ partner.computedName || 'Name Not Set' }}
          </h3>

          <span v-if="partner.account_number" class="text-muted-foreground"
            >Account No. {{ partner.account_number }}</span
          >
        </div>

        <PartnerAssigneeSelect :show-avatar-only="true" :partner />
      </div>

      <div class="flex flex-row lg:items-center items-end gap-2 flex-wrap">
        <PartnerTagSelect :partner />

        <DraftEmailButton v-if="!hideEmailButton" :partner :hide-button-text="false" />

        <DunningStateSelector v-if="partner" :partner="partner" />

        <Tooltip v-if="canSync">
          <TooltipTrigger as-child>
            <Button v-if="canSync" variant="outline" size="sm" :disabled="isSyncing" @click="sync">
              <i class="fa far fa-sync" :class="{ 'animate-spin-slow': isSyncing }" />

              <span> {{ isSyncing ? 'Syncing' : 'Sync' }} </span>
            </Button>
          </TooltipTrigger>

          <TooltipContent>
            Sync the customer from
            {{ partner.linked_integration?.integration?.display_name }}
          </TooltipContent>
        </Tooltip>

        <a
          v-if="partner.remote_payment_portal_url"
          :href="partner.remote_payment_portal_url"
          target="_blank"
        >
          <Button variant="outline" size="sm">
            <i class="far fa-dollar-sign" />

            <span> Payment Portal </span>
          </Button>
        </a>

        <DropdownMenu v-if="profileStore?.profile?.isDisputesEnabled">
          <DropdownMenuTrigger as-child>
            <Button size="icon-sm" variant="outline">
              <i class="fas fa-ellipsis-v" />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end">
            <DropdownMenuGroup>
              <DropdownMenuItem @click="showDisputeFormDialog = true">
                <span>Create Dispute</span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>

    <DisputeFormDialog
      v-if="showDisputeFormDialog"
      v-model:open="showDisputeFormDialog"
      :customer-id="partner.id"
      @close="showDisputeFormDialog = false"
    />
  </div>
</template>

<style scoped></style>
